/**
 * Shared WebSocket protocol between client and server.
 *
 * All messages travel as JSON on a single WebSocket per (room, viewer) pair.
 * Discriminated unions on `type`. Runtime validation via Zod; TS types derived
 * from the schemas so client and server cannot drift.
 *
 * See docs/SYNC_PROTOCOL.md for the canonical reference.
 */
import { z } from 'zod';

// ============================================================================
// Primitive helpers
// ============================================================================

/** Unix milliseconds (Date.now()). Non-negative finite number. */
export const TimestampMs = z.number().finite().nonnegative();

/** Video position in seconds. Non-negative finite. */
export const PositionSeconds = z.number().finite().nonnegative();

/** A short string identifier (room id, connection id, video id). */
export const Id = z.string().min(1).max(128);

// ============================================================================
// Client → Server messages
// ============================================================================

export const ClockSyncRequestSchema = z.object({
  type: z.literal('clock_sync_request'),
  client_send_time: TimestampMs,
});
export type ClockSyncRequest = z.infer<typeof ClockSyncRequestSchema>;

export const PlayMsgSchema = z.object({
  type: z.literal('play'),
  at_position: PositionSeconds,
});
export type PlayMsg = z.infer<typeof PlayMsgSchema>;

export const PauseMsgSchema = z.object({
  type: z.literal('pause'),
  at_position: PositionSeconds,
});
export type PauseMsg = z.infer<typeof PauseMsgSchema>;

export const SeekMsgSchema = z.object({
  type: z.literal('seek'),
  target_position: PositionSeconds,
});
export type SeekMsg = z.infer<typeof SeekMsgSchema>;

export const ReadyReasonSchema = z.enum(['join', 'seek', 'buffer']);
export type ReadyReason = z.infer<typeof ReadyReasonSchema>;

export const ReadyMsgSchema = z.object({
  type: z.literal('ready'),
  reason: ReadyReasonSchema,
});
export type ReadyMsg = z.infer<typeof ReadyMsgSchema>;

export const BufferingMsgSchema = z.object({
  type: z.literal('buffering'),
  at_position: PositionSeconds,
});
export type BufferingMsg = z.infer<typeof BufferingMsgSchema>;

export const HeartbeatMsgSchema = z.object({
  type: z.literal('heartbeat'),
  client_time: TimestampMs,
  client_position: PositionSeconds,
});
export type HeartbeatMsg = z.infer<typeof HeartbeatMsgSchema>;

export const ClientMessageSchema = z.discriminatedUnion('type', [
  ClockSyncRequestSchema,
  PlayMsgSchema,
  PauseMsgSchema,
  SeekMsgSchema,
  ReadyMsgSchema,
  BufferingMsgSchema,
  HeartbeatMsgSchema,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;
export type ClientMessageType = ClientMessage['type'];

// ============================================================================
// Server → Client messages
// ============================================================================

export const ClockSyncResponseSchema = z.object({
  type: z.literal('clock_sync_response'),
  client_send_time: TimestampMs,
  server_time: TimestampMs,
});
export type ClockSyncResponse = z.infer<typeof ClockSyncResponseSchema>;

export const PlayStateSchema = z.enum(['playing', 'paused']);
export type PlayState = z.infer<typeof PlayStateSchema>;

export const RoomStateMsgSchema = z.object({
  type: z.literal('room_state'),
  video_id: Id,
  video_url: z.string().min(1),
  position: PositionSeconds,
  state: PlayStateSchema,
  /** Server wall-clock at which `position` was captured (for drift math). */
  anchor_server_time: TimestampMs,
  members: z.number().int().nonnegative(),
  playback_rate: z.number().finite().positive().default(1.0),
});
export type RoomStateMsg = z.infer<typeof RoomStateMsgSchema>;

export const ScheduledActionSchema = z.enum(['play', 'pause', 'seek']);
export type ScheduledAction = z.infer<typeof ScheduledActionSchema>;

export const ScheduleEventMsgSchema = z.object({
  type: z.literal('schedule_event'),
  action: ScheduledActionSchema,
  target_position: PositionSeconds,
  execute_at_server_time: TimestampMs,
});
export type ScheduleEventMsg = z.infer<typeof ScheduleEventMsgSchema>;

export const WaitForReadyMsgSchema = z.object({
  type: z.literal('wait_for_ready'),
  reason: z.enum(['buffering', 'seek']),
  waiting_count: z.number().int().nonnegative(),
});
export type WaitForReadyMsg = z.infer<typeof WaitForReadyMsgSchema>;

export const ForceResyncMsgSchema = z.object({
  type: z.literal('force_resync'),
  target_position: PositionSeconds,
});
export type ForceResyncMsg = z.infer<typeof ForceResyncMsgSchema>;

export const MemberCountMsgSchema = z.object({
  type: z.literal('member_count'),
  count: z.number().int().nonnegative(),
});
export type MemberCountMsg = z.infer<typeof MemberCountMsgSchema>;

export const ErrorMsgSchema = z.object({
  type: z.literal('error'),
  code: z.string().min(1),
  message: z.string(),
});
export type ErrorMsg = z.infer<typeof ErrorMsgSchema>;

export const ServerMessageSchema = z.discriminatedUnion('type', [
  ClockSyncResponseSchema,
  RoomStateMsgSchema,
  ScheduleEventMsgSchema,
  WaitForReadyMsgSchema,
  ForceResyncMsgSchema,
  MemberCountMsgSchema,
  ErrorMsgSchema,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;
export type ServerMessageType = ServerMessage['type'];

// ============================================================================
// Parsing helpers
// ============================================================================

/**
 * Parse a JSON string into a validated ClientMessage.
 * Returns a discriminated result so callers don't need to wrap in try/catch.
 */
export function parseClientMessage(
  raw: string,
): { ok: true; msg: ClientMessage } | { ok: false; error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: `invalid JSON: ${(e as Error).message}` };
  }
  const result = ClientMessageSchema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, error: result.error.issues.map((i) => i.message).join(', ') };
  }
  return { ok: true, msg: result.data };
}

/** Parse a JSON string into a validated ServerMessage. */
export function parseServerMessage(
  raw: string,
): { ok: true; msg: ServerMessage } | { ok: false; error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: `invalid JSON: ${(e as Error).message}` };
  }
  const result = ServerMessageSchema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, error: result.error.issues.map((i) => i.message).join(', ') };
  }
  return { ok: true, msg: result.data };
}

/** Encode a ServerMessage to a JSON string for sending. */
export function encodeServerMessage(msg: ServerMessage): string {
  return JSON.stringify(msg);
}

/** Encode a ClientMessage to a JSON string for sending. */
export function encodeClientMessage(msg: ClientMessage): string {
  return JSON.stringify(msg);
}

// ============================================================================
// Constants — visible to both sides so they stay in sync.
// ============================================================================

/** How far in the future to schedule synchronized events (>= worst-case RTT). */
export const SCHEDULE_LEAD_MS = 200;

/** Heartbeat interval (ms). */
export const HEARTBEAT_INTERVAL_MS = 2000;

/** Drift correction loop interval (ms). */
export const DRIFT_LOOP_MS = 500;

/**
 * Drift threshold below which no correction is applied (seconds).
 *
 * Must be comfortably above the measurement noise floor: `video.currentTime`
 * granularity, `setTimeout` jitter, and clock-offset uncertainty together can
 * easily produce ±100 ms of apparent drift on a perfectly-synced stream.
 * A deadband below that noise floor causes constant unnecessary playback-rate
 * corrections (audible pitch wobble + audio resampling glitches).
 */
export const DRIFT_IGNORE_S = 0.2;

/** Above this drift, slow/speed up playback rate (seconds). */
export const DRIFT_RATE_S = 0.75;

/** Above this drift, hard-seek (seconds). */
export const DRIFT_HARD_SEEK_S = 1.5;

/** Max playback-rate deviation when slewing (fraction of 1.0). */
export const DRIFT_RATE_MAX_DELTA = 0.03;

/** Proportional gain for rate slew: rate = 1 - drift * gain (clamped). */
export const DRIFT_RATE_GAIN = 0.2;

/** Number of clock-sync rounds on initial connect. */
export const CLOCK_SYNC_ROUNDS_INITIAL = 8;

/** Number of clock-sync rounds on periodic refresh. */
export const CLOCK_SYNC_ROUNDS_REFRESH = 4;

/** Interval between clock-sync rounds within a batch (ms). */
export const CLOCK_SYNC_ROUND_INTERVAL_MS = 200;

/** How often to re-run clock-sync after connect (ms). */
export const CLOCK_SYNC_REFRESH_INTERVAL_MS = 30_000;

/** Hard cap on viewers per room. */
export const MAX_VIEWERS_PER_ROOM = 4;

/** Hard cap on concurrent active rooms across the deployment. */
export const MAX_CONCURRENT_ROOMS = 3;
